import React from 'react';
import { Link } from 'react-router-dom';

export default function BunkerTerms() {
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-zinc-950/80 backdrop-blur-md border-b border-zinc-800/50 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="text-lg font-mono text-zinc-400 hover:text-white transition-colors">
            ← Back to Portfolio
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-24 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Use</h1>
            <p className="text-zinc-400 text-lg">Bunker - Whiskey Collection & Tasting Journal</p>
            <p className="text-zinc-500 text-sm mt-2">Last updated: July 20, 2026</p>
          </div>

          <div className="space-y-10 text-zinc-300 leading-relaxed">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Agreement</h2>
              <p>
                By downloading or using Bunker (&quot;the App&quot;), you agree to these Terms of Use. If
                you do not agree, please do not use the App. The App is distributed through the
                Apple App Store, and your download and any purchases are also subject to Apple&apos;s
                terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Age Requirement</h2>
              <p>
                Bunker is a collection journal about whiskey and is intended solely for people of
                legal drinking age in their jurisdiction. By using the App you confirm that you
                meet that requirement. The App does not sell alcohol, does not facilitate the
                purchase of alcohol, and does not encourage consumption — please enjoy whiskey
                responsibly.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">License</h2>
              <p>
                We grant you a personal, non-exclusive, non-transferable license to use the App on
                devices you own or control, for your personal, non-commercial use. You may not
                copy, modify, distribute, sell, or reverse-engineer the App except where permitted
                by law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Your Data</h2>
              <p>
                All content you create in the App (your collection, prices, pours, tasting notes,
                hunt list, and photos) belongs to you and is stored on your device. You are
                responsible for backing it up using the App&apos;s export feature or the optional
                cloud backup. Deleting the App deletes its local data, and we have no way to
                recover it for you unless you have a cloud backup.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Bunker Premium Subscription</h2>
              <p className="mb-4">
                Bunker Premium is an optional auto-renewable monthly subscription that unlocks
                additional features (such as Friends, bottle suggestions, Barrel Spin, and cloud
                backup). Payment is charged to your Apple ID at confirmation of purchase.
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>The subscription automatically renews each month unless canceled at least 24 hours before the end of the current period</li>
                <li>Manage or cancel anytime in your device Settings → Apple ID → Subscriptions</li>
                <li>Any unused portion of the period is not refunded when you cancel; access continues until the period ends</li>
                <li>Prices are shown in the App and on the App Store payment sheet and may vary by region</li>
              </ul>
              <p className="mt-4">
                Some premium features (Friends, cloud backup) also require an optional account and
                a network connection; we may modify, suspend, or discontinue cloud features with
                reasonable notice.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Accounts &amp; Acceptable Use</h2>
              <p>
                Accounts are optional and require a valid email address. You are responsible for
                keeping your credentials secure and for activity on your account. When using
                social features, you agree not to impersonate others, use offensive handles, or
                abuse the friend system. We may suspend or remove accounts that violate these
                Terms. You can delete your account at any time from within the App, which removes
                your data from our servers.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Whiskey Data &amp; Trademarks</h2>
              <p>
                The App includes a catalog of factual information about published whiskeys (names,
                distilleries, proofs, age statements, typical retail prices, and similar details).
                All bottle and distillery names are trademarks of their respective owners. Bunker
                is an independent app and is not affiliated with, endorsed by, or sponsored by any
                distillery or spirits brand. Bottle images generated by the App are original
                artwork, not brand labels. Prices and estimated values shown in the App are
                informational only, are largely entered by you, and are not offers to buy or sell.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Barcode Lookups</h2>
              <p>
                Barcode identification relies in part on third-party product databases. We do not
                guarantee that any particular barcode will be recognized, and results from
                third-party databases may occasionally be inaccurate. The App always lets you
                identify a bottle manually.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Disclaimer of Warranties</h2>
              <p>
                The App is provided &quot;as is&quot; and &quot;as available,&quot; without warranties of any kind,
                express or implied, including fitness for a particular purpose and
                non-infringement. We do not warrant that the App or its cloud features will be
                uninterrupted or error-free.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, we shall not be liable for any indirect,
                incidental, special, consequential, or exemplary damages arising from your use of
                the App, including loss of data. Our total liability for any claim relating to the
                App is limited to the amount you paid for it in the twelve months preceding the
                claim.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Changes</h2>
              <p>
                We may update the App and these Terms from time to time. Material changes will be
                reflected by revising the &quot;Last updated&quot; date above. Continued use of the App
                after changes constitutes acceptance of the updated Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Contact</h2>
              <p>
                Questions about these Terms? Contact us at{' '}
                <a
                  href="mailto:parkerdevwork@gmail.com"
                  className="text-amber-400 hover:text-amber-300 underline"
                >
                  parkerdevwork@gmail.com
                </a>.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-zinc-800/50">
        <div className="max-w-4xl mx-auto text-center text-sm text-zinc-600">
          <p>© 2026 Bunker. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
